import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';

/**
 * Opciones por turno: combina tardanza e inasistencia en un único selector.
 * Permite T1=tardanza, T2=inasistencia al mismo tiempo.
 *
 * Valores:
 *   'normal'                   → Sin incidencia (regla de tolerancia)
 *   'tardanza_justificada'     → Tardanza justificada (puntual, sin descuento)
 *   'tardanza_descuento'       → Tardanza con descuento (deducir minutos)
 *   'inasistencia_justificada' → Inasistencia justificada (sin descuento)
 *   'inasistencia_descuento'   → Inasistencia con descuento (½ día de sueldo)
 */

const OPCIONES_TURNO = [
    { value: 'normal',                   label: 'Normal (regla de tolerancia)' },
    { value: 'tardanza_justificada',     label: 'Tardanza — Justificada (sin descuento)' },
    { value: 'tardanza_descuento',       label: 'Tardanza — Con descuento de minutos' },
    { value: 'inasistencia_justificada', label: 'Inasistencia — Justificada (sin descuento)' },
    { value: 'inasistencia_descuento',   label: 'Inasistencia — Con descuento (½ día)' },
];

const OPCIONES_SALIDA = [
    { value: 'normal',      label: 'Normal / Salida con Descuento' },
    { value: 'justificada', label: 'Salida Remunerada (Justificada)' },
];

/** Deriva el valor del dropdown a partir de los flags guardados en BD */
function derivarEstadoTurno({ justificado, aplicaTardanza, inasistencia, estadoGlobal, hasNewFlags }) {
    // Nueva lógica con inasistencia1/2
    if (inasistencia === 1) return 'inasistencia_descuento';
    if (inasistencia === 2) return 'inasistencia_justificada';
    // Tardanza
    if (justificado === 1) return 'tardanza_justificada';
    if (aplicaTardanza === 1) return 'tardanza_descuento';
    // Fallback legacy: estado 4 = tardanza justificada
    if (estadoGlobal === 4) return 'tardanza_justificada';
    
    // Fallback legacy: estado 2/3 solo si no existen flags nuevos en este registro
    if (!hasNewFlags) {
        if (estadoGlobal === 2) return 'inasistencia_descuento';
        if (estadoGlobal === 3) return 'inasistencia_justificada';
    }
    
    return 'normal';
}

function ModalObservaciones({
    isOpen,
    value,
    currentState,
    aplicaTardanza,
    aplicaTardanza1,
    aplicaTardanza2,
    justificado1,
    justificado2,
    inasistencia1,
    inasistencia2,
    justificado_salida1,
    justificado_salida2,
    nombreColaborador,
    hasSecondShift,
    onSave,
    onClose,
    canEdit
}) {
    const [text, setText] = useState(value || '');

    // Estado independiente por turno — todo en un solo dropdown
    // Estado independiente por turno
    const [turno1, setTurno1] = useState('normal');
    const [turno2, setTurno2] = useState('normal');
    const [salida1, setSalida1] = useState('normal');
    const [salida2, setSalida2] = useState('normal');

    useEffect(() => {
        if (isOpen) {
            setText(value || '');

            const hasNewFlags = Number(inasistencia1 || 0) > 0 || Number(inasistencia2 || 0) > 0;

            setTurno1(derivarEstadoTurno({
                justificado:     Number(justificado1 || 0),
                aplicaTardanza:  Number(aplicaTardanza1 || 0) || Number(aplicaTardanza || 0),
                inasistencia:    Number(inasistencia1 || 0),
                estadoGlobal:    currentState,
                hasNewFlags
            }));

            if (hasSecondShift) {
                setTurno2(derivarEstadoTurno({
                    justificado:    Number(justificado2 || 0),
                    aplicaTardanza: Number(aplicaTardanza2 || 0) || Number(aplicaTardanza || 0),
                    inasistencia:   Number(inasistencia2 || 0),
                    estadoGlobal:   currentState,
                    hasNewFlags
                }));
            } else {
                setTurno2('normal');
            }

            setSalida1(Number(justificado_salida1 || 0) === 1 ? 'justificada' : 'normal');
            setSalida2(Number(justificado_salida2 || 0) === 1 ? 'justificada' : 'normal');
        }
    }, [isOpen, value, currentState, aplicaTardanza, aplicaTardanza1, aplicaTardanza2,
        justificado1, justificado2, inasistencia1, inasistencia2, justificado_salida1, justificado_salida2, hasSecondShift]);

    const handleSave = () => {
        let finalEstado        = 1;
        let finalJustificado1  = 0;
        let finalJustificado2  = 0;
        let finalAplicaTard1   = 0;
        let finalAplicaTard2   = 0;
        let finalInasistencia1 = 0;
        let finalInasistencia2 = 0;
        let finalJustSal1      = 0;
        let finalJustSal2      = 0;

        // ── Turno 1 ──────────────────────────────────────────────────────
        if (turno1 === 'tardanza_justificada')     finalJustificado1  = 1;
        if (turno1 === 'tardanza_descuento')        finalAplicaTard1   = 1;
        if (turno1 === 'inasistencia_descuento')    finalInasistencia1 = 1;
        if (turno1 === 'inasistencia_justificada')  finalInasistencia1 = 2;

        // ── Turno 2 (si existe) ───────────────────────────────────────────
        if (hasSecondShift) {
            if (turno2 === 'tardanza_justificada')    finalJustificado2  = 1;
            if (turno2 === 'tardanza_descuento')       finalAplicaTard2   = 1;
            if (turno2 === 'inasistencia_descuento')   finalInasistencia2 = 1;
            if (turno2 === 'inasistencia_justificada') finalInasistencia2 = 2;
        }

        // ── Salidas ──────────────────────────────────────────────────────
        if (salida1 === 'justificada') finalJustSal1 = 1;
        if (hasSecondShift && salida2 === 'justificada') finalJustSal2 = 1;

        // ── Calcular estado global ────────────────────────────────────────
        const t1EsInasistencia = turno1.startsWith('inasistencia');
        const t2EsInasistencia = hasSecondShift && turno2.startsWith('inasistencia');
        const hayInasistencia  = t1EsInasistencia || t2EsInasistencia;
        const hayDescuento     = turno1 === 'inasistencia_descuento'
                              || (hasSecondShift && turno2 === 'inasistencia_descuento');

        if (hayInasistencia) {
            finalEstado = hayDescuento ? 2 : 3;
        } else {
            // Tardanza: estado 4 solo si ambos turnos están justificados (legacy)
            const t1JustTardanza = turno1 === 'tardanza_justificada';
            const t2JustTardanza = !hasSecondShift || turno2 === 'tardanza_justificada';
            if (t1JustTardanza && t2JustTardanza) finalEstado = 4;
            else finalEstado = 1;
        }

        onSave(
            text,
            finalEstado,
            0, // aplica_tardanza global (legacy, siempre 0)
            finalJustificado1,
            finalJustificado2,
            finalAplicaTard1,
            finalAplicaTard2,
            finalInasistencia1,
            finalInasistencia2,
            finalJustSal1,
            finalJustSal2
        );
    };

    const colorTurno = (val) => {
        if (val.startsWith('inasistencia_descuento'))    return { border: '#f87171', bg: '#fff5f5', label: '#b91c1c' };
        if (val.startsWith('inasistencia_justificada'))  return { border: '#93c5fd', bg: '#eff6ff', label: '#1d4ed8' };
        if (val.startsWith('tardanza_descuento'))         return { border: '#fcd34d', bg: '#fffbeb', label: '#92400e' };
        if (val.startsWith('tardanza_justificada'))       return { border: '#86efac', bg: '#f0fdf4', label: '#166534' };
        return { border: '#ddd', bg: '#f9f9f9', label: '#666' };
    };

    const renderTurno = (label, value, onChange, salValue, onSalChange) => {
        const c = colorTurno(value);
        const cs = salValue === 'justificada' ? { border: '#86efac', bg: '#f0fdf4', label: '#166534' } : { border: '#ddd', bg: '#f9f9f9', label: '#666' };

        return (
            <div style={{
                border: `1px solid #dee2e6`,
                borderRadius: '12px',
                padding: '15px',
                marginBottom: '20px',
                backgroundColor: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <h6 style={{ fontWeight: 'bold', color: '#0549a7', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                    {label.toUpperCase()}
                </h6>
                
                <div style={{ padding: '10px', borderRadius: '8px', backgroundColor: c.bg, border: `1px solid ${c.border}`, marginBottom: '10px' }}>
                    <Label style={{ fontWeight: 'bold', fontSize: '11px', color: c.label, textTransform: 'uppercase' }}>
                        INGRESO / ESTADO:
                    </Label>
                    <Input
                        type="select"
                        className="form-control"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={!canEdit}
                        style={{ borderColor: c.border }}
                    >
                        {OPCIONES_TURNO.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </Input>
                </div>

                <div style={{ padding: '10px', borderRadius: '8px', backgroundColor: cs.bg, border: `1px solid ${cs.border}` }}>
                    <Label style={{ fontWeight: 'bold', fontSize: '11px', color: cs.label, textTransform: 'uppercase' }}>
                        SALIDA ANTICIPADA:
                    </Label>
                    <Input
                        type="select"
                        className="form-control"
                        value={salValue}
                        onChange={(e) => onSalChange(e.target.value)}
                        disabled={!canEdit}
                        style={{ borderColor: cs.border }}
                    >
                        {OPCIONES_SALIDA.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </Input>
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" centered>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>OBSERVACIONES / JUSTIFICACIÓN</h1>
                    <Button className="close-btn" onClick={onClose}>X</Button>
                </div>
            </ModalHeader>
            <div className="modal-body-footer">
                <ModalBody className="modal-body">
                    <Form className="fila" onSubmit={(e) => e.preventDefault()}>

                        <FormGroup>
                            <Label>Colaborador:</Label>
                            <Input
                                type="text"
                                value={nombreColaborador ? nombreColaborador.toUpperCase() : '—'}
                                disabled
                                className="form-control"
                                style={{ fontWeight: 'bold', backgroundColor: '#ced4da' }}
                            />
                        </FormGroup>

                        <p style={{
                            fontSize: '11px', color: '#6b7280',
                            marginBottom: '10px', fontStyle: 'italic'
                        }}>
                            Selecciona la condición de cada turno de forma independiente.
                        </p>

                        {renderTurno('Turno I', turno1, setTurno1, salida1, setSalida1)}
                        {hasSecondShift && renderTurno('Turno II', turno2, setTurno2, salida2, setSalida2)}

                        <FormGroup>
                            <Label style={{ fontWeight: 'normal' }}>Observaciones:</Label>
                            <Input
                                className="form-control"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Ingrese detalles (opcional)"
                                disabled={!canEdit}
                                style={{ borderRadius: '15px' }}
                            />
                        </FormGroup>

                        {canEdit && (
                            <FormGroup>
                                <div className="button-container">
                                    <Button
                                        className="boton-principal"
                                        onClick={() => {
                                            if (window.confirm('¿Está seguro de registrar estos cambios?')) {
                                                handleSave();
                                            }
                                        }}
                                        style={{ width: 'auto', minWidth: '150px', marginTop: '10px', borderRadius: '10px' }}
                                    >
                                        REGISTRAR
                                    </Button>
                                </div>
                            </FormGroup>
                        )}
                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
}

export default ModalObservaciones;
